import { ChevronRight } from "@mui/icons-material";
import { Box, Button, ButtonBase, Grid2, Paper, Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";

import {
    GqlDashboardFieldworkerLatestFeedback,
    GqlPlannerArea,
    GqlPlannerV2Visit,
    GqlPlannerVisitV2WorkerReport,
    GqlProtocol,
} from "~/__generated__";
import { parseStringToDate } from "~/modules/app/util/dateHelpers";
import { formatDate } from "~/modules/app/util/formatters";

type LatestFeedbackProps = {
    component: GqlDashboardFieldworkerLatestFeedback;
};

export default function DashboardFieldworkerLatestFeedback({ component }: LatestFeedbackProps) {
    const { t } = useTranslation("app");

    return (
        <Grid2 size={{ xs: 12, lg: 6 }}>
            <Paper variant="outlined" sx={{ padding: 2 }}>
                <Stack direction="row" justifyContent="space-between" paddingBottom={2}>
                    <Typography variant="h4">
                        {t("dashboardComponents.dashboardFieldworkerLatestFeedback.latestFeedback")}
                    </Typography>
                    <Button endIcon={<ChevronRight />} variant="text" component={Link} to={"/fieldworker/feedback/"} size="small">
                        {t("dashboardComponents.dashboardFieldworkerLatestFeedback.btnGoToFeedback")}
                    </Button>
                </Stack>
                {component.reportsWithFeedback.length > 0 ? (
                    <PopulatedComponent reportsWithFeedback={component.reportsWithFeedback} />
                ) : (
                    <EmptyComponent />
                )}
            </Paper>
        </Grid2>
    );
}

function EmptyComponent() {
    const { t } = useTranslation("app");
    return <Typography>{t("dashboardComponents.dashboardFieldworkerLatestFeedback.noRecentFeedback")}</Typography>;
}

type PopulatedComponentProps = {
    reportsWithFeedback: (Pick<GqlPlannerVisitV2WorkerReport, "id"> & {
        plannerVisit?:
            | (Pick<GqlPlannerV2Visit, "startTime"> & {
                  plannerArea?: Pick<GqlPlannerArea, "name"> | null;
                  protocol?: Pick<GqlProtocol, "name"> | null;
              })
            | null;
    })[];
};

function PopulatedComponent({ reportsWithFeedback }: PopulatedComponentProps) {
    return (
        <Stack direction="column" rowGap={2}>
            {reportsWithFeedback.map(report => (
                <ButtonBase key={report.id} component={Link} to={`fieldworker/feedback/${report.id}`}>
                    <Box flex={1}>
                        {report.plannerVisit?.startTime && (
                            <Typography variant="subtitle2">
                                {formatDate(parseStringToDate(report.plannerVisit?.startTime as string))}
                            </Typography>
                        )}
                        <Typography color="primary.main" variant="h5">
                            {report.plannerVisit?.plannerArea?.name}
                        </Typography>
                        <Typography color="text.secondary" variant="subtitle1">
                            {report.plannerVisit?.protocol?.name}
                        </Typography>
                    </Box>
                    <ChevronRight />
                </ButtonBase>
            ))}
        </Stack>
    );
}
